import clsx from 'clsx';
import React, { ChangeEvent, forwardRef, HTMLProps, ReactNode } from 'react';
import { useUniqueId } from '../../hooks/use-unique-id';
import { CheckboxInput } from '../checkbox-input';
import { Stack } from '../stack';
import { Typography } from '../typography';
import classes from './checkbox.module.scss';

export interface CheckboxProps extends Omit<React.HTMLProps<HTMLInputElement>, 'label'> {
  /**
   * Custom label for the checkbox
   */
  label?: React.ReactNode;

  /**
   * Checked state
   */
  checked?: boolean;

  /**
   * Render as disabled
   */
  disabled?: boolean;

  /**
   * Custom classname
   */
  className?: string;

  /**
   * Custom change handler
   */
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;

  /**
   * Executes the provided callback function when the value of a checkbox or switch is changed.
   */
  onValueChange?: (checked: boolean) => void;

  /**
   *
   */
  variant?: 'compact';

  /**
   * Custom label tag props for wrapping label tag.
   */
  labelProps?: Omit<HTMLProps<HTMLLabelElement>, 'color' | 'instance' | 'ref'>;

  /**
   * Indicates whether an error has occurred.
   * The variable is optional and, if defined, is a boolean.
   * A value of `true` represents the presence of an error,
   * while `false` indicates the absence of any error.
   */
  error?: boolean;

  /**
   * An optional ReactNode that serves as a helper or additional contextual content.
   * This property can be used to display supplementary information or assist users in understanding the associated component's purpose or usage.
   */
  helper?: ReactNode;
}

export const Checkbox = forwardRef<HTMLDivElement, CheckboxProps>((
  {
    id,
    label,
    checked = false,
    disabled = false,
    variant,
    className = '',
    onChange,
    labelProps,
    onValueChange,
    error,
    helper,
    ...props
  },
  ref
) => {
  const inputId = useUniqueId('checkbox-input-', id);
  const labelId = useUniqueId('checkbox-label-', id);

  return (
    <Stack
      ref={ref}
      spacing={0}
      direction="column"
      crossAxis="stretch"
      className={clsx(
        classes.root,
        labelProps?.className,
        {
          [classes.error]: error,
          [classes.disabled]: disabled,
          [classes.compact]: variant === 'compact'
        },
        className
      )}
    >
      <div className={classes.wrapper}>
        <CheckboxInput
          id={inputId}
          aria-labelledby={label ? labelId : undefined}
          checked={checked}
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          color={error ? 'danger' : 'black'}
          className={classes.checkbox}
          disabled={disabled}
          onChange={e => {
            onChange?.(e);
            onValueChange?.(e.target.checked);
          }}
          {...props}
        />
        <Stack spacing={0.5} direction="column" align="start">
          {label && (
            <Typography {...labelProps} id={labelId} data-testid={'label'} as="label" htmlFor={inputId}
              variant="paragraph" className={classes.label}>
              {label}
            </Typography>
          )}
          {helper && (
            <Typography
              className={classes.helper}
              variant="caption"
              color={error ? 'danger' : 'inherit'}
            >
              {helper}
            </Typography>
          )}
        </Stack>
      </div>

    </Stack>
  );
});
